import React, { useEffect, useRef, useState } from 'react';
import { A4_DIMENSIONS } from './PDFPageBreakHelper';
import './PDFStyles.css';
import { IMAGE_STYLES } from '../../constants/constants';

/**
 * PDFPageContainer - A component to handle page breaks in PDF generation
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The content to be rendered
 * @param {string} props.className - Additional classes for the container
 * @returns {React.ReactElement}
 */
const PDFPageContainer = ({ children, className = '' }) => {
  const { WIDTH, HEIGHT, MARGIN_TOP, MARGIN_BOTTOM, MARGIN_LEFT, MARGIN_RIGHT } = A4_DIMENSIONS;
  
  const containerRef = useRef(null);
  const [pages, setPages] = useState([]);
  
  useEffect(() => {
    // Function to organize content into pages
    const organizeIntoPages = () => {
      if (!containerRef.current) return;
      
      // We need to extract all important elements for pagination
      const extractElements = () => {
        // Get all elements marked with the page-item class for individual pagination
        const pageItems = Array.from(containerRef.current.querySelectorAll('.page-item'));
        
        // If no explicitly marked elements are found, fallback to direct children
        if (pageItems.length === 0) {
          return Array.from(containerRef.current.children);
        }
        
        return pageItems;
      };
      
      // Get all elements we need to process
      const childElements = extractElements();
      
      // Calculate element height including margins
      const getElementTotalHeight = (element) => {
        // Get the computed style of the element
        const style = window.getComputedStyle(element);
        
        // Calculate margins
        const marginTop = parseInt(style.marginTop) || 0;
        const marginBottom = parseInt(style.marginBottom) || 0;
        
        // Special handling for image containers
        if (element.getAttribute('data-key') === 'image-key') {
          // Special handling for image containers is needed because:
          // 1. Images may not have loaded their full dimensions yet
          // 2. We need to account for all images in the container, not just the container itself
          // 3. Image containers can expand dynamically based on content
          // 4. We need to ensure proper page breaks for visual content
          // Get all images inside this container
          const images = element.querySelectorAll('img');
          let totalImageHeight = 0;
          
          // Add base element height without images
          let baseHeight = element.offsetHeight;
          
          // Add estimated height for each image
          images.forEach(img => {
            // Add fixed height per image or use image dimensions if available
            const imgHeight = img.naturalHeight ? 
              Math.min(img.naturalHeight, parseInt(img.style.maxHeight) || IMAGE_STYLES.MAX_HEIGHT) : 
              IMAGE_STYLES.MAX_HEIGHT; // Default to maxHeight value from the style
            
            // Add container padding/margin for each image item
            totalImageHeight += imgHeight + 40; // Adding padding/margins from container
          });
          
          return baseHeight + totalImageHeight + marginTop + marginBottom;
        }
        
        // Default calculation for non-image elements
        const height = element.offsetHeight;
        
        // Return total height including margins
        return height + marginTop + marginBottom;
      };
      
      // Reset pages
      const newPages = [];
      let currentPage = [];
      let currentHeight = MARGIN_TOP;
      
      // Process each child element
      for (let i = 0; i < childElements.length; i++) {
        const child = childElements[i];
        const childHeight = getElementTotalHeight(child);
        
        // Calculate available space on current page
        const availableSpace = HEIGHT - currentHeight - MARGIN_BOTTOM;
        // Check if the element is an image-key element
        
        
        // If element doesn't fit on current page, start a new page
        if (childHeight > availableSpace) {
          // Save current page if not empty
          if (currentPage.length > 0) {
            newPages.push(currentPage);
            currentPage = [];
          }
          
          // Handle elements too large for a single page
          if (childHeight > (HEIGHT - MARGIN_TOP - MARGIN_BOTTOM)) {
            console.log(`Element is too large (${childHeight}px) but will be placed on its own page`);
          }
          
          // Add to new page
          currentPage = [child];
          currentHeight = MARGIN_TOP + childHeight;
          
        } else {
          // Fits on current page
          currentPage.push(child);
          currentHeight += childHeight;

        }
      }
      
      // Add the final page if it has content
      if (currentPage.length > 0) {
        newPages.push(currentPage);
      }
      
      console.log(`Created ${newPages.length} pages`);
      setPages(newPages);
    };
    
    // Run the organization after a brief delay to ensure rendering
    const timer = setTimeout(() => {
      organizeIntoPages();
    }, 200);
    
    // Re-run when window is resized
    window.addEventListener('resize', organizeIntoPages);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', organizeIntoPages);
    };
  }, [children, HEIGHT, MARGIN_TOP, MARGIN_BOTTOM]);
  
  return (
    <>
      {/* Hidden container to measure children */}
      <div 
        ref={containerRef} 
        style={{ 
          position: 'absolute', 
          left: '-9999px', 
          width: WIDTH - MARGIN_LEFT - MARGIN_RIGHT,
          visibility: 'hidden'
        }}
      >
        {children}
      </div>
      
      {/* Render actual pages */}
      <div className="pdf-page-container py-8">
        {pages.map((pageElements, pageIndex) => (
          <div 
            key={`page-${pageIndex}`} 
            className={`pdf-page ${className}`}
            style={{
              width: WIDTH,
              minHeight: HEIGHT,
              maxHeight: HEIGHT,
              margin: '0 auto 20px',
              padding: `${MARGIN_TOP}px ${MARGIN_RIGHT}px ${MARGIN_BOTTOM}px ${MARGIN_LEFT}px`,
              // boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              boxSizing: 'border-box', // Ensure padding is included in the width/height
              // Avoid trailing blank page: only force break when not the last page
              pageBreakAfter: pageIndex < pages.length - 1 ? 'always' : 'auto',
              pageBreakInside: 'avoid'
            }}
          >
            <div className="pdf-page-content">
              {pageElements.map((element, elemIndex) => (
                <div 
                  key={`elem-${pageIndex}-${elemIndex}`}
                  dangerouslySetInnerHTML={{ __html: element.outerHTML }}
                />
              ))}
            </div>
            
            {/* Add page number */}
            <div className="pdf-page-number">Page {pageIndex + 1} of {pages.length}</div>
          </div>
        ))}
      </div>
    </>
  );
};

export default PDFPageContainer; 